import { Controller } from '@hotwired/stimulus'

// Lazy-loads each worklist on the worklists list page (weeks#work_lists).
// Every `.work-list-show` placeholder fetches the rendered worklist card from
// the `get_simple_show` HTML endpoint as soon as it scrolls near the viewport.
// IntersectionObserver drives the scroll detection, with a throttled scroll
// fallback so loading still works if the observer is unavailable or its
// callbacks are deferred. Each worklist loads only once.
export default class extends Controller {
  static targets = ['item']

  connect() {
    this.margin = 600
    this.ticking = false

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(this.handleEntries.bind(this), {
        rootMargin: `${this.margin}px 0px`
      })
      this.itemTargets.forEach((item) => this.observer.observe(item))
    }

    this.boundScroll = this.onScroll.bind(this)
    window.addEventListener('scroll', this.boundScroll, { passive: true })

    // Load anything already in (or near) the viewport without waiting for a
    // scroll or observer callback.
    this.loadVisible()
    this.updateStickyHeadings()
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.boundScroll) window.removeEventListener('scroll', this.boundScroll)
  }

  handleEntries(entries) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        if (this.observer) this.observer.unobserve(entry.target)
        this.loadItem(entry.target)
      }
    })
  }

  onScroll() {
    if (this.ticking) return
    this.ticking = true
    window.requestAnimationFrame(() => {
      this.loadVisible()
      this.updateStickyHeadings()
      this.ticking = false
    })
  }

  loadVisible() {
    this.itemTargets.forEach((item) => {
      if (item.dataset.loaded === 'true') return
      const rect = item.getBoundingClientRect()
      if (rect.bottom >= -this.margin && rect.top <= window.innerHeight + this.margin) {
        if (this.observer) this.observer.unobserve(item)
        this.loadItem(item)
      }
    })
  }

  // Adds a bottom border to a day heading while it is stuck below the top nav
  // (i.e. its section top has passed the nav but the section is still on
  // screen enough to keep the heading pinned).
  updateStickyHeadings() {
    const threshold = 65 // top nav height: h-16 (4rem) + 1px border
    this.element.querySelectorAll('.worklist-date-heading').forEach((heading) => {
      const section = heading.closest('.worklist-day')
      if (!section) return
      const rect = section.getBoundingClientRect()
      const stuck = rect.top <= threshold && rect.bottom > threshold + heading.offsetHeight
      heading.classList.toggle('is-stuck', stuck)
    })
  }

  // Applies the page-level "collapse all notes" state to a freshly loaded
  // worklist card. The state is written by the legacy notes-toggle handler in
  // work-lists.js onto the page element's data-notes-collapsed attribute.
  applyNotesState(item) {
    const collapsed = this.element.dataset.notesCollapsed === 'true'
    if (!collapsed) return
    item.querySelectorAll('.notes-toggle-job').forEach((button) => {
      const row = item.querySelector(`tr.work-list-job-notes[data-job-id="${button.dataset.jobId}"]`)
      if (row) row.classList.add('collapsed')
      button.classList.add('collapsed')
      button.setAttribute('aria-label', 'Expand notes')
    })
  }

  loadItem(item) {
    const id = item.dataset.workListId
    if (!id || item.dataset.loaded === 'true') return
    item.dataset.loaded = 'true'

    fetch(`/work_lists/${id}/get_simple_show`, {
      headers: { Accept: 'text/html' }
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status)
        return response.text()
      })
      .then((html) => {
        item.classList.remove('pending')
        item.innerHTML = html
        this.applyNotesState(item)
      })
      .catch(() => {
        item.dataset.loaded = 'false'
        item.classList.add('work-list-show-error')
        item.innerHTML = 'Could not load this worklist.'
      })
  }
}
